import React from 'react';
import { Card, OverlayTrigger, Tooltip, Table } from 'react-bootstrap';
import Moment from 'react-moment';
import {
  Bloodwork,
  BloodworkAnalysis,
  BloodworkRange,
  PanelField,
  FurtherConsiderations,
  Section,
} from '../../models';
import { UserProfile } from '../../models/UserProfile';
import { DisclaimerSection } from './disclaimer-section';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileDownload } from '@fortawesome/pro-regular-svg-icons';
import { faFileDownload as faFileDownloadSolid } from '@fortawesome/pro-solid-svg-icons';
import { BloodworkTestAnalysisSection } from './bloodwork-test-analysis-section';
import { GenericModal } from '../common/generic-modal';
import { BloodworkEntryStore } from '../../stores/bloodwork-entry-store';
import { BloodworkNotes } from './bloodwork-notes';

type Props = {
  currentUserProfile: UserProfile;
  bloodwork: Bloodwork;
  previousBloodwork?: Bloodwork;
  bloodworkRanges: BloodworkRange[];
  furtherConsiderations: FurtherConsiderations;
  bloodworkEntryStore: BloodworkEntryStore;
};

type State = {
  previousBloodwork: Bloodwork;
  collectionDate: string;
  bloodworkAnalysis: BloodworkAnalysis;
  showUpgradeModal: boolean;
  hideTestResultsAnalysis: boolean;
};

export class BloodworkTestAnalysis extends React.Component<Props, State> {
  state: State = {
    previousBloodwork: undefined,
    collectionDate: '',
    bloodworkAnalysis: undefined,
    showUpgradeModal: false,
    hideTestResultsAnalysis: false,
  };
  pdfWindow: Window;

  componentWillReceiveProps(nextProps: Props) {
    console.log('BloodworkTestAnalysis.componentWillReceiveProps......');
    console.log(nextProps);
    const { bloodwork, previousBloodwork } = nextProps;
    this.analyze(bloodwork, previousBloodwork);
  }

  analyze(bloodwork: Bloodwork, previousBloodwork: Bloodwork) {
    const { bloodworkRanges } = this.props;
    if (bloodwork && bloodworkRanges && bloodworkRanges.length > 0) {
      const bloodworkAnalysis = new BloodworkAnalysis({
        bloodwork,
        bloodworkRanges,
        bloodworkFields: PanelField.ALL_FIELDS,
      });
      bloodworkAnalysis.init().performAnalysis();

      if (previousBloodwork) {
        const previousBloodworkAnalysis = new BloodworkAnalysis({
          bloodwork: previousBloodwork,
          bloodworkRanges,
          bloodworkFields: PanelField.ALL_FIELDS,
        });
        previousBloodworkAnalysis.init();
        bloodworkAnalysis.preparePreviousPanelMarkers(
          previousBloodworkAnalysis,
        );
      }

      this.setState({
        bloodworkAnalysis,
        previousBloodwork: previousBloodwork
          ? new Bloodwork(previousBloodwork)
          : undefined,
      });
      this.scrollToAnalyzeTestResults();
    }
  }

  scrollToAnalyzeTestResults() {
    let element = document.getElementById('test-results-analysis-panel');
    if (element) {
      element.scrollIntoView();
    } else {
      // Hack to resolve issue with scrolling down the first time.
      setTimeout(() => {
        element = document.getElementById('test-results-analysis-panel');
        if (element) {
          element.scrollIntoView();
        }
      }, 1000);
    }
  }

  export = (withNotes = false) => {
    console.log('export button clicked.....');
    const { currentUserProfile } = this.props;
    const { bloodworkAnalysis, previousBloodwork } = this.state;

    // Check to make sure user has this feature enabled
    if (!currentUserProfile.pdfExportAllowed) {
      this.setState({
        showUpgradeModal: true,
      });
    } else {
      sessionStorage.setItem(
        'bloodwork',
        JSON.stringify(bloodworkAnalysis.bloodwork),
      );
      if (previousBloodwork) {
        sessionStorage.setItem(
          'previousBloodwork',
          JSON.stringify(previousBloodwork),
        );
      } else {
        sessionStorage.removeItem('previousBloodwork');
      }
      if (withNotes) {
        sessionStorage.setItem('exportWithNotes', 'true');
      } else {
        sessionStorage.removeItem('exportWithNotes');
      }
      if (this.pdfWindow) {
        this.pdfWindow.close();
      }
      this.pdfWindow = window.open(
        '/bloodwork/analyze/pdf',
        'targetWindow',
        'toolbar=no, location=no, status=no, menubar=no, scrollbars=no, resizable=no, width=1028,height=764',
      );
    }
  };

  exportTooltip() {
    return (
      <Tooltip id="export-tooltip">
        <div>Click this icon to export to PDF</div>
      </Tooltip>
    );
  }

  exportWithNotesTooltip() {
    return (
      <Tooltip id="export-with-notes-tooltip">
        <div>Click this icon to export to PDF with notes</div>
      </Tooltip>
    );
  }

  exportInfoTooltip() {
    return (
      <Tooltip id="export-info-tooltip">
        <div>
          Click this icon to learn more about the export to PDF feature.
        </div>
      </Tooltip>
    );
  }

  openWindow = () => {
    window.open('https://labsmarts.com/testresultsanalysis');
  };

  openExportInfoWindow = () => {
    window.open('https://labsmarts.com/exporttopdf');
  };

  toggleTestResultsAnalysis() {
    this.setState({
      hideTestResultsAnalysis: !this.state.hideTestResultsAnalysis,
    });
  }

  render() {
    console.log('BloodworkTestAnalysis.render......');
    const {
      bloodworkAnalysis,
      showUpgradeModal,
      hideTestResultsAnalysis,
      previousBloodwork,
    } = this.state;
    const { furtherConsiderations, bloodworkEntryStore } = this.props;

    if (!bloodworkAnalysis) {
      return null;
    }

    const wbc = (bloodworkAnalysis as any).wbc;
    const hasWbcValue = wbc?.hasValue?.() === true;

    const collectionDate = bloodworkAnalysis.bloodwork.collectionDate;
    const client = bloodworkAnalysis.bloodwork.client;
    const age = bloodworkAnalysis.bloodwork.clientAgeAtCollectionDate();
    const panelTooltip = (
      <Tooltip id="modal-tooltip">
        Click this icon to learn more about the items in this panel.
      </Tooltip>
    );

    const evalLarge = 3;
    const evalSmall = 12;

    const refLarge = 9;
    const refSmall = 12;

    const hideTestResultsAnalysisIconName = hideTestResultsAnalysis
      ? 'chevron-down'
      : 'chevron-up';

    return (
      <>
        <Card id="test-results-analysis-panel">
          <Card.Header>
            <FontAwesomeIcon
              icon={hideTestResultsAnalysisIconName}
              onClick={() => this.toggleTestResultsAnalysis()}
            />
            &nbsp;&nbsp;Test Results Analysis&nbsp;
            <OverlayTrigger overlay={panelTooltip}>
              <FontAwesomeIcon
                icon="external-link-alt"
                onClick={this.openWindow}
              />
            </OverlayTrigger>
            <span className="float-right">
              <OverlayTrigger
                overlay={this.exportInfoTooltip()}
                placement="bottom"
              >
                <FontAwesomeIcon
                  icon="info-circle"
                  onClick={this.openExportInfoWindow}
                />
              </OverlayTrigger>
              &nbsp;&nbsp;&nbsp;
              <OverlayTrigger overlay={this.exportTooltip()} placement="bottom">
                <FontAwesomeIcon
                  icon={faFileDownload}
                  onClick={() => this.export(false)}
                />
              </OverlayTrigger>
              &nbsp;&nbsp;&nbsp;
              <OverlayTrigger
                overlay={this.exportWithNotesTooltip()}
                placement="bottom"
              >
                <FontAwesomeIcon
                  icon={faFileDownloadSolid}
                  onClick={() => this.export(true)}
                />
              </OverlayTrigger>
            </span>
          </Card.Header>
          {hideTestResultsAnalysis ? null : (
            <Card.Body>
              <Card>
                <Card.Header>Client Information</Card.Header>
                <Card.Body>
                  <div>
                    <Table striped bordered hover className="labsmartz-table">
                      <thead>
                        <tr>
                          <th>Last Name</th>
                          <th>First Name</th>
                          <th>Sex</th>
                          <th>Collection Date</th>
                          <th>Age at Collection</th>
                          <th>Date of Birth</th>
                          <th>Ethnicity</th>
                          <th>Menopause/Pregnant</th>
                          <th>Trimester</th>
                          <th>Elevation</th>
                          <th>Cigarettes Smoked</th>
                          <th>Standard Drinks</th>
                          {previousBloodwork && (
                            <th>Previous Collection Date</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="client-row">
                          <td>{client.lastName}</td>
                          <td>{client.firstName}</td>
                          <td>{client.displayGender}</td>
                          <td>
                            <Moment format="MM/DD/YYYY">
                              {collectionDate}
                            </Moment>
                          </td>
                          <td>{age}</td>
                          <td>
                            <Moment format="MM/DD/YYYY">
                              {client.dateOfBirth}
                            </Moment>
                          </td>
                          <td>{client.displayEthnicity}</td>
                          <td>{client.displayMenopause}</td>
                          <td>{client.displayTrimester}</td>
                          <td>{client.displayElevation}</td>
                          <td>{client.displayCigaretteesSmoked}</td>
                          <td>{client.displayStandardDrinks}</td>
                          {previousBloodwork && (
                            <td>
                              <Moment format="MM/DD/YYYY">
                                {previousBloodwork.collectionDate}
                              </Moment>
                            </td>
                          )}
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
              <BloodworkTestAnalysisSection
                section={Section.RBC}
                panelTitle="Red Blood Cell (RBC) Analysis"
                bloodworkAnalysis={bloodworkAnalysis}
                panelMarkers={bloodworkAnalysis.getRBCPanelMarkers()}
                furtherConsiderations={furtherConsiderations}
                evalSmall={evalSmall}
                refSmall={refSmall}
                evalLarge={evalLarge}
                refLarge={refLarge}
                infoLink="https://labsmarts.com/bibliography/#rbc"
                bloodworkEntryStore={bloodworkEntryStore}
              />

              <BloodworkTestAnalysisSection
                section={Section.IRON}
                panelTitle="Iron Analysis"
                bloodworkAnalysis={bloodworkAnalysis}
                panelMarkers={bloodworkAnalysis.getIronPanelMarkers()}
                furtherConsiderations={furtherConsiderations}
                evalSmall={evalSmall}
                refSmall={refSmall}
                evalLarge={evalLarge}
                refLarge={refLarge}
                infoLink="https://labsmarts.com/bibliography/#iron"
                bloodworkEntryStore={bloodworkEntryStore}
              />

              {hasWbcValue && (
                <BloodworkTestAnalysisSection
                  section={Section.WBC}
                  panelTitle="Immune System Analysis"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={bloodworkAnalysis.getWBCPanelMarkers()}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#wbc"
                  bloodworkEntryStore={bloodworkEntryStore}
                />
              )}

              <BloodworkTestAnalysisSection
                section={Section.PLATELETS}
                panelTitle="Coagulation Analysis"
                bloodworkAnalysis={bloodworkAnalysis}
                panelMarkers={bloodworkAnalysis.getPlateletPanelMarkers()}
                furtherConsiderations={furtherConsiderations}
                evalSmall={evalSmall}
                refSmall={refSmall}
                evalLarge={evalLarge}
                refLarge={refLarge}
                infoLink="https://labsmarts.com/bibliography#platelets"
                bloodworkEntryStore={bloodworkEntryStore}
              />

              <BloodworkTestAnalysisSection
                section={Section.GLUCOSE}
                panelTitle="Glucose Analysis"
                bloodworkAnalysis={bloodworkAnalysis}
                panelMarkers={bloodworkAnalysis.getGlucosePanelMarkers()}
                furtherConsiderations={furtherConsiderations}
                evalSmall={evalSmall}
                refSmall={refSmall}
                evalLarge={evalLarge}
                refLarge={refLarge}
                infoLink="https://labsmarts.com/bibliography/#glucose"
                bloodworkEntryStore={bloodworkEntryStore}
              />

              <BloodworkTestAnalysisSection
                section={Section.KIDNEY}
                panelTitle="Kidney Analysis"
                bloodworkAnalysis={bloodworkAnalysis}
                panelMarkers={bloodworkAnalysis.getKidneyPanelMarkers()}
                furtherConsiderations={furtherConsiderations}
                evalSmall={evalSmall}
                refSmall={refSmall}
                evalLarge={evalLarge}
                refLarge={refLarge}
                infoLink="https://labsmarts.com/bibliography#kidney"
                bloodworkEntryStore={bloodworkEntryStore}
              />

              <BloodworkTestAnalysisSection
                section={Section.PROTEINS}
                panelTitle="Proteins Analysis"
                bloodworkAnalysis={bloodworkAnalysis}
                panelMarkers={bloodworkAnalysis.getProteinsPanelMarkers()}
                furtherConsiderations={furtherConsiderations}
                evalSmall={evalSmall}
                refSmall={refSmall}
                evalLarge={evalLarge}
                refLarge={refLarge}
                infoLink="https://labsmarts.com/bibliography#proteins"
                bloodworkEntryStore={bloodworkEntryStore}
              />

              <BloodworkTestAnalysisSection
                section={Section.NUTRIENTS}
                panelTitle="Nutrients Analysis"
                bloodworkAnalysis={bloodworkAnalysis}
                panelMarkers={bloodworkAnalysis.getMineralPanelMarkers()}
                furtherConsiderations={furtherConsiderations}
                evalSmall={evalSmall}
                refSmall={refSmall}
                evalLarge={evalLarge}
                refLarge={refLarge}
                infoLink="https://labsmarts.com/bibliography#nutrients"
                bloodworkEntryStore={bloodworkEntryStore}
              />

              <BloodworkTestAnalysisSection
                section={Section.ELECTROLYTES}
                panelTitle="Electrolytes Analysis"
                bloodworkAnalysis={bloodworkAnalysis}
                panelMarkers={bloodworkAnalysis.getElectrolytePanelMarkers()}
                furtherConsiderations={furtherConsiderations}
                evalSmall={evalSmall}
                refSmall={refSmall}
                evalLarge={evalLarge}
                refLarge={refLarge}
                infoLink="https://labsmarts.com/bibliography#electrolytes"
                bloodworkEntryStore={bloodworkEntryStore}
              />

              <BloodworkTestAnalysisSection
                section={Section.LIVER_BILIARY}
                panelTitle="Liver / Biliary Analysis"
                bloodworkAnalysis={bloodworkAnalysis}
                panelMarkers={bloodworkAnalysis.getLiverPanelMarkers()}
                furtherConsiderations={furtherConsiderations}
                evalSmall={evalSmall}
                refSmall={refSmall}
                evalLarge={evalLarge}
                refLarge={refLarge}
                infoLink="https://labsmarts.com/bibliography#liver"
                bloodworkEntryStore={bloodworkEntryStore}
              />

              <BloodworkTestAnalysisSection
                section={Section.LIPIDS}
                panelTitle="Lipids Analysis"
                bloodworkAnalysis={bloodworkAnalysis}
                panelMarkers={bloodworkAnalysis.getLipidsPanelMarkers()}
                furtherConsiderations={furtherConsiderations}
                evalSmall={evalSmall}
                refSmall={refSmall}
                evalLarge={evalLarge}
                refLarge={refLarge}
                infoLink="https://labsmarts.com/bibliography#lipids"
                bloodworkEntryStore={bloodworkEntryStore}
              />

              <BloodworkTestAnalysisSection
                section={Section.INFLAMMATION_OXIDATIVE_STRESS}
                panelTitle="Inflammation / Oxidative Stress Analysis"
                bloodworkAnalysis={bloodworkAnalysis}
                panelMarkers={bloodworkAnalysis.getInflammationOxidativeStressPanelMarkers()}
                furtherConsiderations={furtherConsiderations}
                evalSmall={evalSmall}
                refSmall={refSmall}
                evalLarge={evalLarge}
                refLarge={refLarge}
                infoLink="https://labsmarts.com/bibliography#inflammation"
                bloodworkEntryStore={bloodworkEntryStore}
              />

              <BloodworkTestAnalysisSection
                section={Section.THYROID}
                panelTitle="Thyroid Analysis"
                bloodworkAnalysis={bloodworkAnalysis}
                panelMarkers={bloodworkAnalysis.getThyroidPanelMarkers()}
                furtherConsiderations={furtherConsiderations}
                evalSmall={evalSmall}
                refSmall={refSmall}
                evalLarge={evalLarge}
                refLarge={refLarge}
                infoLink="https://labsmarts.com/bibliography#thyroid"
                bloodworkEntryStore={bloodworkEntryStore}
              />

              <BloodworkTestAnalysisSection
                section={Section.SEX_HORMONE}
                panelTitle="Hormones Analysis"
                bloodworkAnalysis={bloodworkAnalysis}
                panelMarkers={bloodworkAnalysis.getSexHormonePanelMarkers()}
                furtherConsiderations={furtherConsiderations}
                evalSmall={evalSmall}
                refSmall={refSmall}
                evalLarge={evalLarge}
                refLarge={refLarge}
                infoLink="https://labsmarts.com/bibliography#hormones"
                bloodworkEntryStore={bloodworkEntryStore}
              />

              <Card>
                <Card.Header>Practitioner Notes</Card.Header>
                <Card.Body>
                  <BloodworkNotes
                    section={Section.NOTES}
                    title="Add your own notes here. Click Save when finished."
                    bloodwork={bloodworkAnalysis.bloodwork}
                    bloodworkEntryStore={bloodworkEntryStore}
                  />
                </Card.Body>
              </Card>
            </Card.Body>
          )}
        </Card>

        <GenericModal
          show={showUpgradeModal}
          title="Export to PDF Feature Not Available"
          text="Export to PDF feature is not available with a free account."
          link="https://labsmarts.com/pricing"
          linkText="Click here to upgrade."
          onHide={() => {
            this.setState({ showUpgradeModal: false });
          }}
        />

        <DisclaimerSection furtherConsiderations={furtherConsiderations} />
      </>
    );
  }
}
